import React, { useState } from 'react';
import { submitForReview } from '../../services/api';

function SubmitButton({ versionId, onValidate, onSubmitSuccess }) {
  const [submitting, setSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [progress, setProgress] = useState('');

  const handleSubmit = async () => {
    // Validate before submitting
    const isValid = onValidate();
    if (!isValid) {
      return;
    }

    setShowConfirm(true);
  };

  const confirmSubmit = async () => {
    setSubmitting(true);
    setProgress('Preparing submission...');
    
    try {
      const response = await submitForReview(versionId);
      
      if (response.data.success) {
        setProgress('Submission successful!');
        
        // Build success message
        let successMessage = '✅ App submitted for review successfully!\n\n';
        
        if (response.data.versionState) {
          successMessage += `Version state: ${response.data.versionState}\n`;
        }
        
        if (response.data.details?.message) {
          successMessage += `\n${response.data.details.message}`;
        }
        
        alert(successMessage);
        setShowConfirm(false);
        setProgress('');
        onSubmitSuccess();
      } else {
        // Show detailed error information
        setProgress('');
        const error = response.data.error || 'Unknown error';
        const details = response.data.details;
        const suggestion = response.data.suggestion;
        
        let errorMessage = `❌ Failed to submit: ${error}`;
        
        if (suggestion) {
          errorMessage += `\n\n💡 Suggestion: ${suggestion}`;
        }
        
        if (details && typeof details === 'object') {
          if (Array.isArray(details)) {
            errorMessage += '\n\n📋 Details:';
            details.forEach((detail, index) => {
              if (typeof detail === 'object') {
                errorMessage += `\n${index + 1}. ${detail.type || detail.title || ''}: ${detail.message || detail.detail || JSON.stringify(detail)}`;
              } else {
                errorMessage += `\n${index + 1}. ${detail}`;
              }
            });
          } else if (details.message) {
            errorMessage += `\n\n${details.message}`;
          } else if (details.currentState) {
            errorMessage += `\n\nCurrent state: ${details.currentState}`;
            if (details.submittableStates) {
              errorMessage += `\nRequired states: ${details.submittableStates.join(', ')}`;
            }
          }
        }
        
        alert(errorMessage);
      }
    } catch (err) {
      setProgress('');
      let errorMessage = '❌ Failed to submit for review\n\n';
      
      if (err.response?.data?.error) {
        errorMessage += `Error: ${err.response.data.error}`;
        
        // Add suggestion if available
        if (err.response.data.suggestion) {
          errorMessage += `\n\n💡 Suggestion: ${err.response.data.suggestion}`;
        }
        
        // Add details if available
        if (err.response.data.details) {
          const details = err.response.data.details;
          
          if (Array.isArray(details)) {
            errorMessage += '\n\n📋 Details:';
            details.forEach((detail, index) => {
              if (typeof detail === 'object') {
                const title = detail.type || detail.title || detail.code || '';
                const message = detail.message || detail.detail || JSON.stringify(detail);
                errorMessage += `\n${index + 1}. ${title ? title + ': ' : ''}${message}`;
              } else {
                errorMessage += `\n${index + 1}. ${detail}`;
              }
            });
          } else if (typeof details === 'object') {
            if (details.message) {
              errorMessage += `\n\n${details.message}`;
            } else if (details.currentState) {
              errorMessage += `\n\nCurrent state: ${details.currentState}`;
              if (details.submittableStates) {
                errorMessage += `\nRequired states: ${details.submittableStates.join(', ')}`;
              }
            } else {
              errorMessage += `\n\n${JSON.stringify(details, null, 2)}`;
            }
          }
        }
      } else {
        errorMessage += err.message || 'Network error occurred';
      }
      
      alert(errorMessage);
    } finally {
      setSubmitting(false);
      setProgress('');
    }
  };

  return (
    <div className="submit-section">
      <div className="submit-section-info">
        <h3>Ready to Submit?</h3>
        <p>Make sure all required information is filled in before submitting your app for review.</p>
      </div>
      <button
        className="btn btn-success btn-lg"
        onClick={handleSubmit}
        disabled={submitting}
      >
        Submit for Review
      </button>

      {showConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Confirm Submission</h2>
              <button className="modal-close" onClick={() => setShowConfirm(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <p className="text-secondary">
                Are you sure you want to submit this version for App Store review? 
                Once submitted, you won't be able to make changes until the review is complete.
              </p>
              
              <div className="alert alert-warning">
                <div>
                  <p style={{ marginBottom: '8px' }}>
                    <strong>Before you submit, verify:</strong>
                  </p>
                  <ul style={{ paddingLeft: '20px', marginBottom: 0, fontSize: '13px' }}>
                    <li>App information and localization are complete</li>
                    <li>Screenshots are uploaded for required sizes</li>
                    <li>Age rating is configured</li>
                    <li>Review information is provided</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-ghost"
                onClick={() => setShowConfirm(false)}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                className="btn btn-success"
                onClick={confirmSubmit}
                disabled={submitting}
                style={{ minWidth: '150px' }}
              >
                {submitting ? (progress || 'Submitting...') : 'Confirm Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SubmitButton;
